import { useCallback, useEffect, useRef, useState } from 'react';

import axiosClient from '../../api/axios-client';

/**
 * Le cycle de vie de l'abonnement push mobile — #78, décisions #20 et #25.
 *
 * ## Pourquoi un hook à part et non `hooks/useWebPush.ts`
 *
 * Celui du web enregistre `/sw-push.js` **en dur** : deux origines, deux service
 * workers (#14), et une registration est liée au chemin de son script. Il fait
 * aussi un aller-retour réseau au pire endroit — voir « clé VAPID » plus bas. On
 * duplique donc, comme la règle du dépôt le veut sur les chemins critiques ;
 * l'importer aurait de toute façon franchi la frontière du socle (#23).
 *
 * ## Les quatre gestes, et pourquoi chacun existe
 *
 * **1. Réabonnement systématique au démarrage.** C'est le geste le plus important
 * du ticket, et il ne répare rien de visible. Il tue le **quatrième état** que #20
 * nomme comme le plus dangereux — *permission accordée, abonnement mort* :
 * endpoint purgé sur un 410, sanction de push silencieux de WebKit, ou
 * déconnexion. L'employé se croit joignable, il ne l'est plus, et l'écran d'état
 * afficherait « installée » à un manager qui s'entendrait répondre « mais si, je
 * l'ai ». Avec le réabonnement, cet état **se répare seul** à la prochaine
 * ouverture, sans que personne ait rien remarqué.
 *
 * **2. Déclaration de l'état de permission**, au même démarrage. Le serveur ne
 * voit qu'« abonné » ou « rien » : il ne peut pas séparer *jamais installée* de
 * *refusée*. Envoyée même — surtout — quand elle vaut `denied`, cas où il n'y a
 * aucun abonnement à envoyer.
 *
 * **3. Réabonnement à la bascule d'établissement** — devenu sans effet sur la
 * portée : le serveur abonne l'appareil à TOUS les hôtels du compte, quelle que
 * soit la sélection (retour Odomo, 2026-09-29). Gardé parce qu'il ne coûte
 * qu'un POST idempotent et qu'il ressuscite une ligne purgée au passage.
 *
 * **4. La clé VAPID préchargée.** `useWebPush.subscribe()` du web fait
 * `await axiosClient.get('/web-push/vapid-key')` **entre** `requestPermission()`
 * et `pushManager.subscribe()` — exactement l'attente réseau intercalée que la
 * précaution n° 3 de #13 interdit, et qui fait échouer l'abonnement sur iOS quand
 * le lien est lent. Ici la clé est chargée au montage ; `abonner()` ne touche
 * plus au réseau avant d'appeler `subscribe()`.
 */

const CHEMIN_SERVICE_WORKER = '/sw-mobile.js';

export type EtatPermission = 'granted' | 'denied' | 'default';

export interface AbonnementPush {
    /** Le navigateur sait-il faire du push ? Faux dans un onglet iOS. */
    supporte: boolean;
    permission: EtatPermission | 'indisponible';
    /** Demande la permission puis abonne. À n'appeler que depuis un geste utilisateur. */
    demanderEtAbonner: () => Promise<EtatPermission>;
    /** Réabonne sans rien demander — la bascule d'établissement, le démarrage. */
    reabonner: () => Promise<void>;
    /** Coupe l'abonnement. Appelé à la déconnexion. */
    desabonner: () => Promise<void>;
    /** Relit la permission système (après une amorce montée ailleurs). */
    relirePermission: () => void;
}

function supportDisponible(): boolean {
    return (
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
    );
}

/** La clé VAPID arrive en base64url ; `applicationServerKey` veut des octets. */
function base64UrlVersOctets(base64: string): Uint8Array {
    const bourrage = '='.repeat((4 - (base64.length % 4)) % 4);
    const normalise = (base64 + bourrage).replace(/-/g, '+').replace(/_/g, '/');
    const brut = window.atob(normalise);
    const octets = new Uint8Array(brut.length);
    for (let i = 0; i < brut.length; i += 1) octets[i] = brut.charCodeAt(i);
    return octets;
}

export function useAbonnementPush(): AbonnementPush {
    const supporte = supportDisponible();

    const [permission, setPermission] = useState<EtatPermission | 'indisponible'>(() =>
        supporte ? (Notification.permission as EtatPermission) : 'indisponible',
    );

    /**
     * La clé, en ref et non en state : elle ne se rend jamais, et la lire depuis
     * un state forcerait `abonner()` à dépendre du cycle de rendu — donc à parfois
     * s'exécuter avec la valeur d'avant.
     */
    const cleVapid = useRef<string | null>(null);

    useEffect(() => {
        if (!supporte) return;

        // Préchargement (#13, précaution n° 3). L'échec est silencieux : `abonner()`
        // saura le redemander, ce qui est moins bon mais pas cassé.
        void axiosClient
            .get('/web-push/vapid-key')
            .then(({ data }) => {
                cleVapid.current = data?.public_key ?? null;
            })
            .catch(() => {
                cleVapid.current = null;
            });
    }, [supporte]);

    /** L'abonnement lui-même, sans jamais demander la permission. */
    const abonner = useCallback(async (): Promise<void> => {
        const registration = await navigator.serviceWorker.register(CHEMIN_SERVICE_WORKER);

        if (!cleVapid.current) {
            const { data } = await axiosClient.get('/web-push/vapid-key');
            cleVapid.current = data?.public_key ?? null;
        }
        if (!cleVapid.current) {
            throw new Error('Clé VAPID non configurée côté serveur');
        }

        // Un abonnement déjà en place est réutilisé : `subscribe()` avec la même
        // clé rend le même endpoint. On le renvoie quand même au serveur — c'est
        // tout l'intérêt du réabonnement systématique, qui doit ressusciter une
        // ligne purgée côté base sans que l'appareil s'en aperçoive.
        const abonnement =
            (await registration.pushManager.getSubscription()) ??
            (await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: base64UrlVersOctets(cleVapid.current),
            }));

        const { endpoint, keys } = abonnement.toJSON();

        // L'établissement n'est PAS envoyé : le serveur abonne l'appareil à tous
        // les hôtels du compte, jamais à la seule sélection affichée.
        await axiosClient.post('/web-push/subscribe', { endpoint, keys });
    }, []);

    const declarerPermission = useCallback(async (etat: EtatPermission): Promise<void> => {
        // Le serveur ne voit qu'« abonné » ou « rien » : c'est cette déclaration
        // qui distingue « jamais installée » de « refusée » (#20 §6).
        await axiosClient.post('/web-push/mobile-permission', { permission: etat }).catch(() => {
            /* Sans réseau, l'écran d'état sera périmé — jamais bloquant. */
        });
    }, []);

    const demanderEtAbonner = useCallback(async (): Promise<EtatPermission> => {
        if (!supporte) return 'denied';

        const etat = (await Notification.requestPermission()) as EtatPermission;
        setPermission(etat);

        // L'ABONNEMENT D'ABORD, la déclaration ensuite.
        //
        // L'ordre inverse mettait le POST de déclaration très exactement entre
        // `requestPermission()` et `pushManager.subscribe()` — l'attente réseau
        // intercalée que la précaution n° 3 de #13 interdit, et la raison pour
        // laquelle la clé VAPID est préchargée. Le défaut a été trouvé par le test
        // d'ordre, pas à la relecture : c'est précisément pour ça qu'il existe.
        //
        // La déclaration n'est que de la tenue de registre pour l'écran de #80 ;
        // elle peut attendre une seconde, l'abonnement non.
        if (etat === 'granted') await abonner();

        await declarerPermission(etat);

        return etat;
    }, [supporte, abonner, declarerPermission]);

    const reabonner = useCallback(async (): Promise<void> => {
        if (!supporte) return;

        const etat = Notification.permission as EtatPermission;
        setPermission(etat);
        await declarerPermission(etat);

        // La boîte système n'est JAMAIS présentée ici : un « Ne pas autoriser » iOS
        // est irrécupérable depuis l'app (#79). On ne réabonne que l'accordé.
        if (etat !== 'granted') return;

        await abonner().catch(() => {
            /* Le prochain démarrage réessaiera : c'est tout le principe. */
        });
    }, [supporte, abonner, declarerPermission]);

    const desabonner = useCallback(async (): Promise<void> => {
        if (!supporte) return;

        try {
            const registration = await navigator.serviceWorker.getRegistration();
            const abonnement = await registration?.pushManager.getSubscription();
            if (!abonnement) return;

            // Le serveur d'abord : si l'ordre était inverse et le réseau absent, la
            // ligne resterait en base et pousserait le nom d'un client sur l'écran
            // verrouillé de quelqu'un qui s'est déconnecté.
            await axiosClient.delete('/web-push/unsubscribe', {
                data: { endpoint: abonnement.endpoint },
            });
            await abonnement.unsubscribe();
        } catch {
            /* Déconnexion best-effort : elle ne doit jamais retenir l'utilisateur. */
        }
    }, [supporte]);

    /** Relit `Notification.permission` — l'amorce (#80) l'a peut-être changée ailleurs. */
    const relirePermission = useCallback(() => {
        if (supporte) setPermission(Notification.permission as EtatPermission);
    }, [supporte]);

    return { supporte, permission, demanderEtAbonner, reabonner, desabonner, relirePermission };
}
